<template>
  <div class="intelligence-page">
    <div
      v-if="showWelcome"
      class="welcome-backdrop"
      @click.self="dismissWelcome"
      @keydown.esc="dismissWelcome"
      @keydown.tab.prevent="$refs.welcomeDismiss?.focus()"
    >
      <section
        class="welcome-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ceremony-intelligence-welcome"
      >
        <span class="welcome-mark" aria-hidden="true">✦</span>
        <div class="welcome-copy">
          <p class="welcome-eyebrow">New feature</p>
          <h3 id="ceremony-intelligence-welcome">Welcome to Ceremony Intelligence</h3>
          <p>
            Search ceremony footage, explore useful moments and export the clips you need.
          </p>
          <div class="welcome-tips">
            <h4>Tips for better results</h4>
            <ul>
              <li>
                <strong>Describe the moment</strong>
                Include the action and a distinctive detail, such as “graduate dancing in a red suit”.
              </li>
              <li>
                <strong>Search a name</strong>
                Enter it as announced to find matching transcript moments.
              </li>
              <li>
                <strong>Search the season</strong>
                Use the wider search when you are not sure which ceremony contains the moment.
              </li>
            </ul>
          </div>
          <p class="welcome-help">
            Something missing? Send your account manager the ceremony and exact words you
            searched. If a result is wrong, use its feedback buttons too.
          </p>
        </div>
        <button
          ref="welcomeDismiss"
          type="button"
          class="welcome-dismiss"
          @click="dismissWelcome"
        >
          Start exploring
        </button>
      </section>
    </div>

    <header class="page-heading">
      <p class="page-eyebrow">Ceremony Intelligence</p>
      <h2>Find the moments that matter</h2>
      <p>Search, browse and export useful moments from your ceremonies.</p>
    </header>

    <section class="search-surface">
      <ceremony-search-controls
        :allowed-universities="allowedUniversities"
        :selected-slug="selectedSlug"
        :ceremonies="ceremonies"
        :ceremonies-loading="ceremoniesLoading"
        :ceremony-error="ceremonyError"
        :selected-ceremony-id="selectedCeremonyId"
        :search-scope="searchScope"
        :ceremony-seasons="ceremonySeasons"
        :selected-season-key="selectedSeasonKey"
        :query-text="queryText"
        :can-submit-search="canSubmitSearch"
        :format-ceremony="formatCeremony"
        :readiness="ceremonyReadiness"
        @update:selectedSlug="selectedSlug = $event"
        @update:selectedCeremonyId="selectedCeremonyId = $event"
        @update:searchScope="searchScope = $event"
        @update:selectedSeasonKey="selectedSeasonKey = $event"
        @update:queryText="queryText = $event"
        @university-change="onUniversityChange"
        @ceremony-change="onCeremonyChange"
        @search-scope-change="onSearchScopeChange"
        @season-change="onSeasonChange"
        @submit-search="submitSearch"
      />
    </section>

    <section
      v-if="recentExports.length"
      class="recent-exports"
      aria-labelledby="recent-exports-heading"
    >
      <div class="recent-exports-heading">
        <div>
          <p class="recent-exports-eyebrow">Your clips</p>
          <h3 id="recent-exports-heading">Recent exports</h3>
        </div>
        <p>Recent export history is saved in this browser for 7 days.</p>
      </div>
      <ul class="recent-export-list">
        <li v-for="item in recentExports" :key="item.exportKey" class="recent-export-item">
          <div class="recent-export-copy">
            <strong>{{ item.request.label }}</strong>
            <span>
              {{ formatDuration(item.request.startSeconds) }}–{{ formatDuration(item.request.endSeconds) }}
              · {{ Math.round(item.request.endSeconds - item.request.startSeconds) }} sec
            </span>
          </div>
          <div class="recent-export-actions">
            <span class="recent-export-status" :class="`is-${item.statusTone}`">
              {{ item.statusLabel }}
            </span>
            <button
              v-if="item.status === 'COMPLETE'"
              type="button"
              class="recent-export-button"
              @click="recordExportDownload({ exportKey: item.exportKey, exportId: item.export_id })"
            >
              Download
            </button>
            <button
              v-else-if="item.isActivelyProcessing"
              type="button"
              class="recent-export-button"
              disabled
            >
              Preparing…
            </button>
            <button
              v-else
              type="button"
              class="recent-export-button"
              @click="retryRecentExport(item)"
            >
              Try again
            </button>
          </div>
        </li>
      </ul>
    </section>

    <section
      v-if="selectedCeremonyId"
      class="suggested-section"
      aria-labelledby="suggested-moments-heading"
    >
      <ceremony-highlights
        :highlights="ceremonyHighlights"
        :is-loading="ceremonyHighlightsLoading"
        :selected-event-id="selectedEventId"
        :active-video-url="activeVideoUrl"
        :format-duration="formatDuration"
        :readiness="ceremonyReadiness"
        @select-highlight="selectHighlight"
      />
    </section>

    <section v-if="selectedCeremonyId" class="ceremony-workspace">
      <aside class="workspace-guide">
        <ceremony-chapter-navigator
          :chapters="guideChapters"
          :search-results="eventsData"
          :search-active="hasSubmittedSearch"
          :search-loading="eventsLoading"
          :search-error="eventsUploadError"
          :search-notice="searchScopeNotice"
          :selected-event-id="selectedEventId"
          :format-duration="formatDuration"
          @select-item="selectWorkspaceItem"
          @show-guide="showCeremonyGuide"
        />
      </aside>

      <div class="workspace-player">
        <ceremony-media-player
          ref="mediaPlayer"
          :active-video-url="activeVideoUrl"
          :ceremony-video-loading="ceremonyVideoLoading"
          :ceremony-video-error="ceremonyVideoError"
          :video-options="videoOptions"
          :readiness="ceremonyReadiness"
          @loadeddata="onPlayerLoadedData"
        />
      </div>

      <aside class="workspace-details">
        <ceremony-event-details
          :active-event="activeEvent"
          :ceremony-id="selectedCeremonyId"
          :events-loading="eventsLoading"
          :event-feedback="eventFeedback[activeEvent.id] || ''"
          :feedback-submitting="Boolean(feedbackSubmitting[activeEvent.id])"
          :feedback-error="feedbackErrors[activeEvent.id] || ''"
          :format-duration="formatDuration"
          :export-states="eventExports"
          :active-video-url="activeVideoUrl"
          :video-options="videoOptions"
          :ceremony-video-duration="ceremonyVideoDuration"
          :cut-times="ceremonyCutTimes"
          @submit-feedback="recordEventFeedback"
          @export-modal-open="pauseWorkspacePlayer"
          @export-event="requestEventExport"
          @download-export="recordExportDownload"
        />
      </aside>
    </section>

    <ceremony-processing-controls
      v-if="isAdmin"
      :selected-slug="selectedSlug"
      :selected-ceremony-id="selectedCeremonyId"
      :ceremonies="ceremonies"
      :format-ceremony="formatCeremony"
      :readiness="ceremonyReadiness"
      :can-build-chapters="canBuildChapters"
      :context-processing="contextProcessing"
      :context-message="contextMessage"
      :context-error="contextError"
      :chapter-action-label="chapterActionLabel"
      @rebuild-chapters="requestChapterBackfill"
      @current-readiness="onCurrentReadiness"
    />
  </div>
</template>

<script>
import axios from 'axios'
import 'video.js/dist/video-js.css'
import Auth from '../../api/auth.js'
import { trackAdminEvent } from '../../admin/activity.js'
import { buildCeremonySeasons } from '../../admin/ceremonySeasons.js'
import CeremonyChapterNavigator from './search/CeremonyChapterNavigator.vue'
import CeremonyEventDetails from './search/CeremonyEventDetails.vue'
import CeremonyHighlights from './search/CeremonyHighlights.vue'
import CeremonyMediaPlayer from './search/CeremonyMediaPlayer.vue'
import CeremonySearchControls from './search/CeremonySearchControls.vue'
import CeremonyProcessingControls from './search/CeremonyProcessingControls.vue'
import {
  SECTION_EXPORT_POLL_DEADLINE_MS,
  SECTION_EXPORT_POLL_INITIAL_MS,
  SECTION_EXPORT_POLL_MAX_MS,
  createSectionExport,
  getSectionExport,
  isSectionExportPending,
  loadRecentSectionExports,
  saveRecentSectionExports,
} from '../../api/sectionExports.js'

const WELCOME_STORAGE_KEY = 'mgc:ceremony-intelligence-welcome:v1'

export default {
  name: 'CeremonySearch',
  components: {
    CeremonyChapterNavigator,
    CeremonyEventDetails,
    CeremonyHighlights,
    CeremonyMediaPlayer,
    CeremonyProcessingControls,
    CeremonySearchControls,
  },
  data() {
    const payload = Auth.getPayload()
    const tokenUniversities = Array.isArray(payload?.universities) ? payload.universities : []
    const tokenRoles = Array.isArray(payload?.roles) ? payload.roles : []
    return {
      tokenUniversities,
      isAdmin: Boolean(payload?.admin || tokenRoles.includes('admin')),
      allowedUniversities: [],
      selectedSlug: tokenUniversities[0] || '',
      ceremonies: [],
      ceremoniesLoading: false,
      ceremonyError: '',
      selectedCeremonyId: '',
      searchScope: 'ceremony',
      selectedSeasonKey: '',
      searchScopeNotice: '',
      ceremonyVideoLoading: false,
      ceremonyVideoError: '',
      ceremonyVideoUrl: '',
      ceremonyVideoType: '',
      ceremonyVideoDuration: 0,
      ceremonyCutTimes: [],
      queryText: '',
      eventsUploadError: '',
      eventsLoading: false,
      eventsData: [],
      hasSubmittedSearch: false,
      selectedEventId: '',
      eventFeedback: {},
      feedbackSubmitting: {},
      feedbackErrors: {},
      currentSearchId: '',
      eventPlaybackTimer: null,
      exportPollTimers: {},
      eventExports: {},
      exportStorageScope: payload?.tenant && payload?.sub
        ? `${payload.tenant}:${payload.sub}`
        : '',
      top_k: 20,
      ceremonyHighlights: [],
      ceremonyHighlightsLoading: false,
      ceremonyChapters: [],
      contextProcessing: false,
      contextMessage: '',
      contextError: '',
      contextPollTimer: null,
      contextPollDeadline: 0,
      ceremonyContextRequestId: 0,
      ceremonyReadiness: 'idle',
      showWelcome: sessionStorage.getItem(WELCOME_STORAGE_KEY) !== 'seen',
    }
  },
  computed: {
    selectedUniversity() {
      return (this.allowedUniversities || []).find(u => u.slug === this.selectedSlug) || null
    },
    canSubmitSearch() {
      return Boolean(
        !this.eventsLoading &&
        this.searchCeremonyIds.length &&
        String(this.queryText || '').trim()
      )
    },
    ceremonySeasons() {
      return buildCeremonySeasons(this.ceremonies)
    },
    selectedSeason() {
      return this.ceremonySeasons.find(season => season.key === this.selectedSeasonKey) || null
    },
    searchCeremonyIds() {
      if (this.searchScope === 'season') {
        return this.selectedSeason?.ceremonyIds || []
      }
      return this.selectedCeremonyId ? [this.selectedCeremonyId] : []
    },
    activeVideoUrl() {
      return this.ceremonyVideoUrl
    },
    activeVideoType() {
      if (this.ceremonyVideoType) return this.ceremonyVideoType
      const url = String(this.activeVideoUrl || '').toLowerCase().split('?')[0]
      if (url.endsWith('.mp4')) return 'video/mp4'
      return 'application/x-mpegURL'
    },
    activeEvent() {
      return this.allSelectableEvents.find(event => event.id === this.selectedEventId)
        || this.suggestedEvents[0]
        || this.guideChapters[0]
        || {}
    },
    suggestedEvents() {
      return this.ceremonyHighlights
        .map(highlight => this.mapHighlightToEvent(highlight))
        .filter(Boolean)
    },
    guideChapters() {
      return this.ceremonyChapters
        .map((chapter, chapterIndex) => {
          const chapterEvent = this.mapChapterToEvent(chapter, chapterIndex)
          if (!chapterEvent) return null
          return {
            ...chapterEvent,
            events: (chapter.events || [])
              .map((event, eventIndex) => (
                this.mapChapterItemToEvent(event, chapter, chapterIndex, eventIndex)
              ))
              .filter(Boolean),
          }
        })
        .filter(Boolean)
    },
    allSelectableEvents() {
      return [
        ...this.eventsData,
        ...this.suggestedEvents,
        ...this.guideChapters.flatMap(chapter => [chapter, ...(chapter.events || [])]),
      ]
    },
    recentExports() {
      return Object.entries(this.eventExports)
        .map(([exportKey, state]) => {
          const status = String(state.status || '').toUpperCase()
          const isActivelyProcessing = Boolean(
            state.export_id && isSectionExportPending(state)
          )
          return {
            ...state,
            exportKey,
            status,
            isActivelyProcessing,
            statusLabel: status === 'COMPLETE'
              ? 'Ready'
              : (isActivelyProcessing ? 'Preparing' : 'Needs attention'),
            statusTone: status === 'COMPLETE'
              ? 'ready'
              : (isActivelyProcessing ? 'processing' : 'attention'),
          }
        })
        .filter(item => item.request?.ceremonyId && item.request?.label)
        .sort((left, right) => Number(right.saved_at) - Number(left.saved_at))
        .slice(0, 3)
    },
    canBuildChapters() {
      return Boolean(
        this.isAdmin
        && this.activeVideoUrl
        && !this.ceremonyHighlightsLoading
        && !this.contextMessage
      )
    },
    chapterActionLabel() {
      return this.ceremonyChapters.length ? 'Rebuild chapters' : 'Build chapters'
    },
    videoOptions() {
      return {
        autoplay: false,
        controls: true,
        preload: 'auto',
        fluid: false,
        responsive: false,
        width: 1920,
        height: 1080,
        sources: this.activeVideoUrl
          ? [
              {
                type: this.activeVideoType,
                src: this.activeVideoUrl,
              },
            ]
          : [],
      }
    },
  },
  methods: {
    dismissWelcome() {
      this.showWelcome = false
      sessionStorage.setItem(WELCOME_STORAGE_KEY, 'seen')
    },
    newSearchId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID()
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    trackProductEvent(activityType, metadata = {}) {
      return trackAdminEvent(activityType, {
        toolKey: 'ceremony_intelligence',
        metadata,
      })
    },
    resultTrackingMetadata(event) {
      const rank = this.eventsData.findIndex(row => row.id === event?.id)
      const startSeconds = Number(event?.cluster_start_sec ?? event?.windowStartSeconds ?? event?.timestamp)
      const endSeconds = Number(event?.cluster_end_sec ?? event?.windowEndSeconds)
      return {
        searchId: this.currentSearchId,
        ceremonyId: event?.ceremonyId || this.selectedCeremonyId,
        resultType: event?.type || 'scene',
        rank: rank >= 0 ? rank + 1 : null,
        startSeconds: Number.isFinite(startSeconds) ? Math.round(startSeconds) : null,
        endSeconds: Number.isFinite(endSeconds) ? Math.round(endSeconds) : null,
      }
    },
    async initialLoad() {
      await this.fetchUniversities()
      await this.onUniversityChange()
    },
    async loadEventsAPI() {
      if (this.eventsLoading) return

      this.eventsLoading = true
      this.eventsUploadError = ''
      this.searchScopeNotice = ''
      this.eventsData = []
      const base = process.env.VUE_APP_BASE_URL || ''
      const queryUrl = base + '/semantic-search/query'
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const payload = {
        query: this.queryText,
        top_k: this.top_k,
      }
      if (this.searchScope === 'season') payload.ceremony_ids = this.searchCeremonyIds
      else payload.ceremony_id = this.selectedCeremonyId
      const startedAt = Date.now()
      let outcome = 'success'
      let sceneCount = 0
      let transcriptCount = 0
      let exactTranscriptCount = 0
      let fuzzyTranscriptCount = 0
      let chapterResultCount = 0
      let eventResultCount = 0
      let transcriptChunkResultCount = 0
      let transcriptModelId = null
      let visualVerification = {}

      try {
        const query = await axios.post(queryUrl, payload, {
          headers,
          timeout: 28000,
        })
        const eventData = query.data || {}
        const scope = eventData.search_scope || {}
        const skippedCount = Array.isArray(scope.skipped_ceremonies)
          ? scope.skipped_ceremonies.length
          : 0
        const searchedCount = Array.isArray(scope.searched_ceremony_ids)
          ? scope.searched_ceremony_ids.length
          : this.searchCeremonyIds.length
        this.searchScopeNotice = skippedCount
          ? `Searched ${searchedCount} ceremonies. ${skippedCount} ${skippedCount === 1 ? 'was' : 'were'} not ready yet.`
          : ''
        visualVerification = eventData.visual_verification || {}
        transcriptModelId = eventData.transcript_model_id || null
        const rawEvents = Array.isArray(eventData.scenes) ? eventData.scenes.map((item, index) => ({ item, index, source: 'scenes' })) : []
        const transcriptEvents = Array.isArray(eventData.transcript_matches)
          ? eventData.transcript_matches.map((item, index) => ({ item, index, source: 'transcript' }))
          : []
        sceneCount = rawEvents.length
        transcriptCount = transcriptEvents.length
        exactTranscriptCount = transcriptEvents.filter(({ item }) => item?.exact_text_match).length
        fuzzyTranscriptCount = transcriptEvents.filter(({ item }) => (
          item?.lexical_match_type === 'fuzzy'
        )).length
        chapterResultCount = transcriptEvents.filter(({ item }) => item?.candidate_type === 'chapter').length
        eventResultCount = transcriptEvents.filter(({ item }) => item?.candidate_type === 'event').length
        transcriptChunkResultCount = transcriptEvents.filter(({ item }) => (
          !item?.candidate_type || item.candidate_type === 'transcript_chunk'
        )).length
        const exactTranscriptEvents = transcriptEvents.filter(({ item }) => item?.exact_text_match)
        const semanticTranscriptEvents = transcriptEvents.filter(({ item }) => !item?.exact_text_match)
        const rankedEvents = Array.isArray(eventData.results)
          ? eventData.results.map((item, index) => ({
              item,
              index,
              source: item?.match_type === 'transcript' ? 'transcript' : 'scenes',
            }))
          : exactTranscriptEvents.concat(rawEvents, semanticTranscriptEvents)
        const mappedEvents = rankedEvents
          .map(({ item, index, source }) => (
            source === 'scenes'
              ? this.mapSceneToEvent(item, index)
              : this.mapTranscriptMatchToEvent(item, index)
          ))
          .filter(Boolean)

        if (!mappedEvents.length) {
          outcome = 'empty'
          this.eventsData = []
          this.eventsUploadError = 'No matching moments were returned.'
          return
        }

        this.eventsData = mappedEvents
        this.selectedEventId = mappedEvents[0].id
        this.loadResultCeremony(mappedEvents[0])
        this.clearEventPlaybackTimer()
      } catch (error) {
        const timedOut = error?.code === 'ECONNABORTED' || error?.response?.status === 504
        outcome = timedOut
          ? 'timeout'
          : (error?.response?.status === 409 ? 'not_ready' : 'error')
        this.eventsUploadError = timedOut
          ? 'Search is taking longer than expected. Please try again; no work was lost.'
          : error?.response?.data?.message || error?.response?.data?.error || 'Failed to load events.'
      } finally {
        this.eventsLoading = false
        this.trackProductEvent('semantic_search_completed', {
          searchId: this.currentSearchId,
          ceremonyId: this.selectedCeremonyId,
          searchScope: this.searchScope,
          ceremonyCount: this.searchCeremonyIds.length,
          universitySlug: this.selectedSlug,
          outcome,
          latencyMs: Date.now() - startedAt,
          sceneCount,
          transcriptCount,
          exactTranscriptCount,
          fuzzyTranscriptCount,
          chapterResultCount,
          eventResultCount,
          transcriptChunkResultCount,
          transcriptModelId,
          visualVerificationStatus: visualVerification.status || null,
          visualVerificationModelId: visualVerification.model_id || null,
          visualVerificationDurationMs: visualVerification.duration_ms ?? null,
          visualVerificationCandidateCount: visualVerification.candidate_count ?? null,
          visualVerificationRejectedCount: visualVerification.rejected_count ?? null,
          visualVerificationErrorCount: visualVerification.error_count ?? null,
          visualVerificationOrderChanged: visualVerification.order_changed ?? null,
          visualVerificationTopResultChanged: visualVerification.top_result_changed ?? null,
        })
      }
    },
    async fetchUniversities() {
      try {
        const response = await axios.get('/universities')
        const rows = Array.isArray(response.data) ? response.data : []
        const allowed = new Set(this.tokenUniversities)
        this.allowedUniversities = this.isAdmin
          ? rows
          : rows.filter(u => allowed.has(u.slug))
      } catch (_) {
        this.allowedUniversities = (this.tokenUniversities || []).map(slug => ({ slug, name: slug }))
      }

      if (!this.selectedSlug && this.allowedUniversities.length) {
        this.selectedSlug = this.allowedUniversities[0].slug
      }
    },
    async onUniversityChange() {
      this.clearContextPolling()
      this.selectedCeremonyId = ''
      this.selectedSeasonKey = ''
      this.searchScopeNotice = ''
      this.hasSubmittedSearch = false
      this.ceremonyHighlights = []
      this.ceremonyHighlightsLoading = false
      this.ceremonyChapters = []
      this.contextMessage = ''
      this.contextError = ''
      this.ceremonyReadiness = 'idle'
      this.clearCeremonyVideo()
      await this.fetchCeremonies()
      if (this.selectedCeremonyId) {
        await this.onCeremonyChange()
      }
    },
    async onCeremonyChange() {
      this.clearContextPolling()
      this.hasSubmittedSearch = false
      this.ceremonyHighlights = []
      this.ceremonyHighlightsLoading = false
      this.ceremonyChapters = []
      this.contextMessage = ''
      this.contextError = ''
      this.ceremonyReadiness = this.selectedCeremonyId ? 'loading' : 'idle'
      this.clearEvents()
      this.eventsUploadError = ''
      if (this.selectedCeremonyId) {
        this.trackProductEvent('ceremony_selected', {
          ceremonyId: this.selectedCeremonyId,
          universitySlug: this.selectedSlug,
        })
      }
      await this.fetchCeremonyHighlights()
    },
    async onSearchScopeChange(scope) {
      this.searchScopeNotice = ''
      this.clearEvents()
      this.hasSubmittedSearch = false
      if (scope !== 'season') return
      const season = this.ceremonySeasons.find(item => (
        item.ceremonyIds.includes(this.selectedCeremonyId)
      )) || this.ceremonySeasons[0]
      if (season) await this.onSeasonChange(season.key)
    },
    async onSeasonChange(seasonKey) {
      this.selectedSeasonKey = seasonKey
      this.searchScopeNotice = ''
      this.clearEvents()
      this.hasSubmittedSearch = false
      const firstCeremonyId = this.selectedSeason?.ceremonyIds?.[0] || ''
      if (firstCeremonyId && firstCeremonyId !== this.selectedCeremonyId) {
        this.selectedCeremonyId = firstCeremonyId
        await this.onCeremonyChange()
      }
    },
    setEventExportState(exportKey, state) {
      const existingState = this.eventExports[exportKey] || {}
      this.eventExports = {
        ...this.eventExports,
        [exportKey]: {
          ...existingState,
          ...state,
          saved_at: existingState.saved_at || state.saved_at || Date.now(),
        },
      }
      this.persistEventExports()
    },
    restoreEventExports() {
      const restored = loadRecentSectionExports(this.exportStorageScope)
      this.eventExports = restored.reduce((states, item) => ({
        ...states,
        [item.exportKey]: {
          export_id: item.exportId,
          status: item.status,
          saved_at: item.savedAt,
          request: item.request,
          start_seconds: item.request.startSeconds,
          end_seconds: item.request.endSeconds,
          duration_seconds: item.request.endSeconds - item.request.startSeconds,
        },
      }), {})
      this.persistEventExports()
    },
    persistEventExports() {
      saveRecentSectionExports(
        this.exportStorageScope,
        Object.entries(this.eventExports).map(([exportKey, state]) => ({
          exportKey,
          exportId: state.export_id,
          status: state.status,
          savedAt: state.saved_at,
          request: state.request,
        }))
      )
    },
    refreshRecentExports() {
      Object.entries(this.eventExports).forEach(([exportKey, state]) => {
        if (state.export_id) this.pollEventExport(exportKey, state.export_id)
      })
    },
    async retryRecentExport(item) {
      if (!item?.exportKey || !item.request) return
      this.setEventExportState(item.exportKey, {
        status: 'SUBMITTING',
        error: '',
        download_error: '',
      })
      try {
        const result = await createSectionExport(item.request)
        this.applyEventExportResponse(item.exportKey, result.data)
      } catch (error) {
        this.applyEventExportResponse(item.exportKey, {
          status: 'ERROR',
          error: (
            error?.response?.data?.error
            || error?.message
            || 'The section export could not be restarted.'
          ),
        })
      }
    },
    clearExportPolling(exportKey = '') {
      if (exportKey) {
        if (this.exportPollTimers[exportKey]) {
          clearTimeout(this.exportPollTimers[exportKey].timer)
          const remainingTimers = { ...this.exportPollTimers }
          delete remainingTimers[exportKey]
          this.exportPollTimers = remainingTimers
        }
        return
      }

      Object.values(this.exportPollTimers).forEach(poll => clearTimeout(poll.timer))
      this.exportPollTimers = {}
    },
    scheduleExportPoll(exportKey, exportId) {
      const existingPoll = this.exportPollTimers[exportKey]
      if (existingPoll?.timer) clearTimeout(existingPoll.timer)
      const startedAt = existingPoll?.startedAt || Date.now()
      if (Date.now() - startedAt >= SECTION_EXPORT_POLL_DEADLINE_MS) {
        this.clearExportPolling(exportKey)
        this.setEventExportState(exportKey, {
          status: 'CHECK_LATER',
          export_id: exportId,
          error: (
            'This export is taking longer than expected. '
            + 'You can check its status again without starting another export.'
          ),
        })
        return
      }
      const delay = existingPoll
        ? Math.min(existingPoll.delay * 2, SECTION_EXPORT_POLL_MAX_MS)
        : SECTION_EXPORT_POLL_INITIAL_MS
      const timer = setTimeout(() => {
        this.pollEventExport(exportKey, exportId)
      }, delay)
      this.exportPollTimers = {
        ...this.exportPollTimers,
        [exportKey]: { timer, startedAt, delay },
      }
    },
    applyEventExportResponse(exportKey, data) {
      const currentState = this.eventExports[exportKey] || {}
      const previousStatus = String(currentState.status || '').toUpperCase()
      const exportState = { ...currentState, ...(data || {}) }
      this.setEventExportState(exportKey, exportState)
      const nextStatus = String(exportState.status || '').toUpperCase()
      if (
        ['COMPLETE', 'ERROR', 'CANCELED'].includes(nextStatus)
        && previousStatus !== nextStatus
      ) {
        this.trackProductEvent('section_export_completed', {
          ...this.resultTrackingMetadata(exportState.trackingEvent),
          outcome: nextStatus.toLowerCase(),
        })
      }
      if (isSectionExportPending(exportState) && exportState.export_id) {
        this.scheduleExportPoll(exportKey, exportState.export_id)
      } else {
        this.clearExportPolling(exportKey)
      }
    },
    async requestEventExport({
      exportKey,
      ceremonyId,
      eventId,
      event,
      startSeconds,
      endSeconds,
    }) {
      if (
        !exportKey
        || !ceremonyId
        || !eventId
        || !event
        || isSectionExportPending(this.eventExports[exportKey])
      ) return

      this.trackProductEvent('section_export_requested', {
        ...this.resultTrackingMetadata(event),
        durationSeconds: endSeconds - startSeconds,
        startSeconds,
        endSeconds,
      })
      const request = {
        ceremonyId,
        sectionId: eventId,
        label: event.title || 'Ceremony section',
        startSeconds,
        endSeconds,
      }
      this.setEventExportState(exportKey, {
        status: 'SUBMITTING',
        request,
        trackingEvent: event,
        start_seconds: startSeconds,
        end_seconds: endSeconds,
        duration_seconds: endSeconds - startSeconds,
        download_error: '',
      })
      try {
        const result = await createSectionExport(request)
        this.applyEventExportResponse(exportKey, result.data)
      } catch (error) {
        this.applyEventExportResponse(exportKey, {
          status: 'ERROR',
          error: (
            error?.response?.data?.error
            || error?.message
            || 'The section export could not be started.'
          ),
        })
      }
    },
    async pollEventExport(exportKey, exportId) {
      if (!exportKey || !exportId) return
      try {
        const result = await getSectionExport(exportId)
        this.applyEventExportResponse(exportKey, result.data)
      } catch (error) {
        if (error?.response?.status === 503) {
          this.scheduleExportPoll(exportKey, exportId)
          return
        }
        this.applyEventExportResponse(exportKey, {
          status: 'ERROR',
          export_id: exportId,
          error: error?.response?.data?.error || 'The section export status could not be loaded.',
        })
      }
    },
    async recordExportDownload({ exportKey, exportId }) {
      if (!exportKey || !exportId) return
      try {
        const result = await getSectionExport(exportId)
        this.applyEventExportResponse(exportKey, {
          ...result.data,
          download_error: '',
        })
        const downloadUrl = result.data?.download_url
        if (!downloadUrl) throw new Error('The clip download is not ready yet.')
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = ''
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.trackProductEvent('section_export_downloaded', {
          ...this.resultTrackingMetadata(this.eventExports[exportKey]?.trackingEvent),
        })
      } catch (error) {
        this.setEventExportState(exportKey, {
          download_error: (
            error?.response?.data?.error
            || error?.message
            || 'The clip download could not be refreshed.'
          ),
        })
      }
    },
    async fetchCeremonyHighlights() {
      const ceremonyId = this.selectedCeremonyId
      const requestId = ++this.ceremonyContextRequestId
      this.ceremonyHighlights = []
      this.ceremonyChapters = []
      this.ceremonyCutTimes = []
      this.clearCeremonyVideo()
      this.ceremonyReadiness = ceremonyId ? 'loading' : 'idle'
      this.ceremonyVideoLoading = true
      this.ceremonyHighlightsLoading = true
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const queryUrl = base + `/semantic-search/highlights/${ceremonyId}`
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await axios.get(queryUrl, { headers })
        if (
          requestId !== this.ceremonyContextRequestId
          || ceremonyId !== this.selectedCeremonyId
        ) return
        if (response.status === 200) {
          const highlights = Array.isArray(response.data.highlights) ? response.data.highlights : []
          const chapters = Array.isArray(response.data.chapters) ? response.data.chapters : []
          const cutTimes = Array.isArray(response.data.cut_times) ? response.data.cut_times : []
          const preview = response.data.preview || {}
          this.ceremonyHighlights = highlights
          this.ceremonyChapters = chapters
          this.ceremonyCutTimes = cutTimes
          this.ceremonyReadiness = 'ready'
          this.ceremonyVideoUrl = String(preview.url || '')
          this.ceremonyVideoType = String(preview.type || '')
          if (!this.ceremonyVideoUrl) {
            this.ceremonyVideoError = 'No indexed ceremony video is available.'
          }
          const firstSuggested = highlights.length
            ? this.mapHighlightToEvent(highlights[0])
            : null
          const firstChapter = chapters[0]
            ? this.mapChapterToEvent(chapters[0], 0)
            : null
          this.selectedEventId = firstSuggested?.id || firstChapter?.id || ''
        }
      } catch (error) {
        if (
          requestId !== this.ceremonyContextRequestId
          || ceremonyId !== this.selectedCeremonyId
        ) return
        if (error?.response?.status === 409) {
          this.ceremonyReadiness = error?.response?.data?.reason || 'not_started'
        } else {
          this.ceremonyReadiness = 'unavailable'
          this.ceremonyVideoError = (
            error?.response?.data?.error
            || 'Failed to load indexed ceremony context.'
          )
        }
      } finally {
        if (
          requestId === this.ceremonyContextRequestId
          && ceremonyId === this.selectedCeremonyId
        ) {
          this.ceremonyHighlightsLoading = false
          this.ceremonyVideoLoading = false
        }
      }
    },
    async requestChapterBackfill() {
      if (!this.canBuildChapters || this.contextProcessing) return
      this.contextProcessing = true
      this.contextMessage = ''
      this.contextError = ''
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const url = base + `/semantic-search/index-jobs/${this.selectedCeremonyId}/context`
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        await axios.post(url, {}, { headers })
        this.contextMessage = this.ceremonyChapters.length
          ? 'Chapter rebuild started…'
          : 'Chaptering started…'
        this.trackProductEvent('ceremony_chaptering_requested', {
          ceremonyId: this.selectedCeremonyId,
          universitySlug: this.selectedSlug,
        })
        this.contextPollDeadline = Date.now() + (15 * 60 * 1000)
        this.scheduleChapterStatusPoll(this.selectedCeremonyId)
      } catch (error) {
        this.contextError = (
          error?.response?.data?.error
          || 'Chaptering could not be started.'
        )
      } finally {
        this.contextProcessing = false
      }
    },
    clearContextPolling() {
      if (this.contextPollTimer) {
        clearTimeout(this.contextPollTimer)
        this.contextPollTimer = null
      }
      this.contextPollDeadline = 0
    },
    scheduleChapterStatusPoll(ceremonyId) {
      if (!ceremonyId || Date.now() >= this.contextPollDeadline) {
        this.contextMessage = 'Chaptering is still running. Check this ceremony again later.'
        this.clearContextPolling()
        return
      }
      this.contextPollTimer = setTimeout(
        () => this.pollChapterStatus(ceremonyId),
        5000
      )
    },
    async pollChapterStatus(ceremonyId) {
      if (ceremonyId !== this.selectedCeremonyId) {
        this.clearContextPolling()
        return
      }
      try {
        const base = process.env.VUE_APP_BASE_URL || ''
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const response = await axios.get(
          base + `/semantic-search/index-jobs/${ceremonyId}`,
          { headers }
        )
        const job = response.data?.job || {}
        const status = String(
          job.transcript_text_embedding_status
          || job.transcript_embedding_status
          || ''
        ).toUpperCase()
        if (status === 'SUCCEEDED') {
          this.contextMessage = 'Chapters are ready.'
          this.clearContextPolling()
          await this.fetchCeremonyHighlights()
          return
        }
        if (status === 'EMPTY') {
          this.contextMessage = 'No timed transcript sections were available.'
          this.clearContextPolling()
          return
        }
        if (status === 'FAILED' || status === 'SKIPPED') {
          this.contextMessage = ''
          this.contextError = job.transcript_embedding_reason || 'Chaptering failed.'
          this.clearContextPolling()
          return
        }
      } catch (_) {
        // A transient status failure should not start duplicate chaptering work.
      }
      this.scheduleChapterStatusPoll(ceremonyId)
    },
    async fetchCeremonies() {
      this.ceremoniesLoading = true
      this.ceremonyError = ''
      this.ceremonies = []

      const universityId = this.selectedUniversity?.id
      if (!universityId) {
        this.ceremoniesLoading = false
        return
      }

      try {
        const response = await axios.get(`/universities/${universityId}/ceremonies`)
        const rows = Array.isArray(response.data) ? response.data : []
        this.ceremonies = rows.slice().sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
        if (this.ceremonies.length) {
          this.selectedCeremonyId = String(this.ceremonies[0].id)
          this.selectedSeasonKey = this.ceremonySeasons.find(season => (
            season.ceremonyIds.includes(this.selectedCeremonyId)
          ))?.key || ''
        }
      } catch (_) {
        this.ceremonyError = 'Failed to load ceremonies for the selected university.'
      } finally {
        this.ceremoniesLoading = false
      }
    },
    clearCeremonyVideo() {
      this.ceremonyVideoLoading = false
      this.ceremonyVideoError = ''
      this.ceremonyVideoUrl = ''
      this.ceremonyVideoType = ''
      this.ceremonyVideoDuration = 0
    },
    clearEvents() {
      this.eventsData = []
      this.selectedEventId = ''
      this.clearEventPlaybackTimer()
    },
    onPlayerLoadedData() {
      const duration = this.$refs.mediaPlayer?.player?.duration?.()
      if (Number.isFinite(duration) && duration > 0) {
        this.ceremonyVideoDuration = duration
      }
    },
    onCurrentReadiness(readiness) {
      this.ceremonyReadiness = readiness
      if (readiness === 'ready') {
        this.fetchCeremonyHighlights()
      }
    },
    mapSceneToEvent(scene, index) {
      const timestamp = Number(
        scene?.best_frame_timestamp_sec ??
        scene?.best_match_timestamp_sec ??
        scene?.best_match_start_sec ??
        scene?.cluster_start_sec
      )
      if (!Number.isFinite(timestamp)) return null

      const windowStart = Number(
        scene?.best_window_start_sec ??
        scene?.best_match_start_sec ??
        scene?.cluster_start_sec ??
        timestamp
      )
      const windowEnd = Number(
        scene?.best_window_end_sec ??
        scene?.best_match_end_sec ??
        scene?.cluster_end_sec ??
        timestamp
      )
      const durationSeconds = Math.max(1, windowEnd - windowStart)
      const sceneScore = Number(scene?.scene_score ?? scene?.relevance_score ?? scene?.best_score)
      const relativeScore = Number(scene?.relative_relevance_score)
      const peakScore = Number(scene?.peak_score ?? scene?.best_score)
      const supportScore = Number(scene?.support_score)
      const tightnessScore = Number(scene?.tightness_score)
      const distance = Number(scene?.best_distance)
      const anchorHits = Number(scene?.anchor_hit_count)
      const supportingHits = Number(scene?.supporting_hits)
      const eventId = String(scene?.best_key || '').trim() || `scene-event-${index + 1}`
      const ceremonyId = String(scene?.ceremony_id || this.selectedCeremonyId)
      const ceremonyLabel = this.resultCeremonyLabel(ceremonyId)
      const chapterLabel = String(scene?.chapter?.label || '').split('_').join(' ').trim()
      const chapterSummary = String(scene?.chapter?.summary || '').trim()

      return {
        id: eventId,
        ceremonyId,
        ceremonyLabel,
        best_key: eventId,
        title: chapterSummary || `Scene ${index + 1}`,
        timestamp,
        windowStartSeconds: windowStart,
        windowEndSeconds: windowEnd,
        cluster_start_sec: Number.isFinite(Number(scene?.cluster_start_sec)) ? Number(scene.cluster_start_sec) : windowStart,
        cluster_end_sec: Number.isFinite(Number(scene?.cluster_end_sec)) ? Number(scene.cluster_end_sec) : windowEnd,
        artifacts_keys: Array.isArray(scene?.artifacts_keys)
          ? scene.artifacts_keys
          : (Array.isArray(scene?.artifact_keys) ? scene.artifact_keys : []),
        relative_relevance_score: relativeScore,
        type: 'frame',
        score: Number.isFinite(sceneScore) ? sceneScore.toFixed(3) : null,
        distance: Number.isFinite(distance) ? distance : null,
        duration: this.formatDuration(durationSeconds),
        headline: `Start ${this.formatDuration(windowStart)} Duration ${this.formatDuration(durationSeconds)}`,
        summary: `Cluster ${this.formatDuration(windowStart)} to ${this.formatDuration(windowEnd)} • Peak ${this.formatDuration(timestamp)}`,
        description: chapterSummary
          || 'A visual match from this ceremony.',
        metadata: `Score ${Number.isFinite(sceneScore) ? sceneScore.toFixed(3) : 'n/a'} • Relative ${Number.isFinite(relativeScore) ? relativeScore.toFixed(3) : 'n/a'} • Anchor hits ${Number.isFinite(anchorHits) ? anchorHits : 'n/a'} • Supporting hits ${Number.isFinite(supportingHits) ? supportingHits : 'n/a'}`,
        tags: [
          'frame',
          ...(chapterLabel ? [chapterLabel] : []),
          Number.isFinite(peakScore) ? `peak ${peakScore.toFixed(3)}` : 'peak n/a',
          Number.isFinite(supportScore) ? `support ${supportScore.toFixed(3)}` : 'support n/a',
          Number.isFinite(tightnessScore) ? `tightness ${tightnessScore.toFixed(3)}` : 'tightness n/a',
        ],
        evidenceGroup: 'visual',
      }
    },
    chapterTitle(chapter) {
      const summary = String(chapter?.summary || '').trim()
      if (summary) return summary
      return String(chapter?.label || 'Ceremony section').split('_').join(' ')
    },
    chapterEventId(chapter, index) {
      return `chapter-${index}-${Number(chapter?.start || 0)}`
    },
    mapChapterToEvent(chapter, index) {
      const start = Number(chapter?.start)
      const end = Number(chapter?.end)
      if (!Number.isFinite(start)) return null
      const safeEnd = Number.isFinite(end) && end > start ? end : start + 1
      const label = String(chapter?.label || 'section').split('_').join(' ')
      const title = this.chapterTitle(chapter)
      return {
        id: this.chapterEventId(chapter, index),
        title,
        timestamp: start,
        windowStartSeconds: start,
        windowEndSeconds: safeEnd,
        cluster_start_sec: start,
        cluster_end_sec: safeEnd,
        type: 'chapter',
        duration: this.formatDuration(safeEnd - start),
        headline: title,
        summary: `${label} • ${this.formatDuration(start)} to ${this.formatDuration(safeEnd)}`,
        description: '',
        tags: ['chapter', label],
      }
    },
    mapChapterItemToEvent(item, chapter, chapterIndex, eventIndex) {
      const start = Number(item?.start)
      const end = Number(item?.end)
      if (!Number.isFinite(start)) return null
      const safeEnd = Number.isFinite(end) && end > start ? end : start + 1
      const title = String(item?.summary || '').trim() || 'Ceremony moment'
      return {
        id: `chapter-event-${chapterIndex}-${eventIndex}-${start}`,
        chapterId: this.chapterEventId(chapter, chapterIndex),
        title,
        timestamp: start,
        windowStartSeconds: start,
        windowEndSeconds: safeEnd,
        cluster_start_sec: start,
        cluster_end_sec: safeEnd,
        type: 'chapter_event',
        duration: this.formatDuration(safeEnd - start),
        headline: title,
        summary: `${this.chapterTitle(chapter)} • ${this.formatDuration(start)}`,
        description: title,
        tags: ['chapter event'],
      }
    },
    mapTranscriptMatchToEvent(match, index) {
      const start = Number(match?.start_second)
      const end = Number(match?.end_second)
      if (!Number.isFinite(start)) return null
      const safeEnd = Number.isFinite(end) && end > start ? end : start + 1
      const score = Number(match?.score)
      const chapterSummary = String(match?.chapter?.summary || '').trim()
      const chapterLabel = String(match?.chapter?.label || 'transcript').split('_').join(' ')
      const text = String(match?.text || '').trim()
      const candidateType = String(match?.candidate_type || 'transcript_chunk')
      const eventSummary = String(match?.event_summary || '').trim()
      const exactTextMatch = Boolean(match?.exact_text_match)
      const lexicalMatchType = String(match?.lexical_match_type || '')
      const eventId = String(match?.key || '').trim() || `transcript-event-${index + 1}`
      const ceremonyId = String(match?.ceremony_id || this.selectedCeremonyId)
      const ceremonyLabel = this.resultCeremonyLabel(ceremonyId)
      const textTitle = text.length > 88 ? `${text.slice(0, 85).trim()}…` : text

      return {
        id: eventId,
        ceremonyId,
        ceremonyLabel,
        best_key: eventId,
        title: eventSummary || chapterSummary || textTitle || 'Transcript match',
        timestamp: start,
        windowStartSeconds: start,
        windowEndSeconds: safeEnd,
        cluster_start_sec: start,
        cluster_end_sec: safeEnd,
        artifacts_keys: [],
        relative_relevance_score: score,
        type: 'transcript',
        candidateType,
        exactTextMatch,
        evidenceGroup: exactTextMatch
          ? 'exact_spoken'
          : (lexicalMatchType === 'fuzzy' ? 'fuzzy_spoken' : 'spoken'),
        score: Number.isFinite(score) ? score.toFixed(3) : null,
        distance: Number.isFinite(Number(match?.distance)) ? Number(match.distance) : null,
        duration: this.formatDuration(safeEnd - start),
        headline: text || chapterSummary || 'Transcript match',
        summary: `${chapterLabel} • ${this.formatDuration(start)} to ${this.formatDuration(safeEnd)}`,
        description: text || 'Transcript match for this ceremony.',
        metadata: `Hybrid score ${Number.isFinite(score) ? score.toFixed(3) : 'n/a'}`,
        tags: [
          candidateType.split('_').join(' '),
          chapterLabel,
          ...(exactTextMatch ? ['exact text'] : []),
          ...(lexicalMatchType === 'fuzzy' ? ['close text match'] : []),
        ],
      }
    },
    highlightEventId(highlight) {
      const sceneId = String(
        highlight?.scene_id ??
        highlight?.best_key ??
        highlight?.id ??
        ''
      ).trim()
      if (sceneId) return `highlight-${sceneId}`

      const timestamp = Number(
        highlight?.best_frame_timestamp_sec ??
        highlight?.best_match_timestamp_sec ??
        highlight?.start_second
      )
      const sourceHighlightId = String(highlight?.highlight_id || '').trim() || 'highlight'
      return Number.isFinite(timestamp) ? `highlight-${sourceHighlightId}-${timestamp}` : ''
    },
    mapHighlightToEvent(highlight) {
      const timestamp = Number(highlight?.best_frame_timestamp_sec ?? highlight?.start_second)
      const windowStart = Number(highlight?.start_second ?? timestamp)
      const windowEnd = Number(highlight?.end_second ?? timestamp)
      const durationSeconds = Number(highlight?.duration_seconds) > 0
        ? Number(highlight?.duration_seconds)
        : Math.max(1, windowEnd - windowStart)
      const relevanceScore = Number(highlight?.relevance_score)
      const confidenceScore = Number(highlight?.confidence_score)
      const supportingHits = Number(highlight?.supporting_hits)
      const supportingQueryCount = Number(highlight?.supporting_query_count)

      if (!Number.isFinite(timestamp)) return null

      return {
        id: this.highlightEventId(highlight),
        best_key: this.highlightEventId(highlight),
        title: String(highlight?.label || highlight?.highlight_label || 'Suggested moment'),
        kind: ['standout', 'ceremony_moment'].includes(highlight?.kind)
          ? highlight.kind
          : '',
        category: String(highlight?.category || ''),
        timestamp,
        windowStartSeconds: Number.isFinite(windowStart) ? windowStart : timestamp,
        windowEndSeconds: Number.isFinite(windowEnd) ? windowEnd : timestamp + durationSeconds,
        cluster_start_sec: Number.isFinite(windowStart) ? windowStart : timestamp,
        cluster_end_sec: Number.isFinite(windowEnd) ? windowEnd : timestamp + durationSeconds,
        artifacts_keys: Array.isArray(highlight?.artifact_keys) ? highlight.artifact_keys : [],
        relative_relevance_score: Number.isFinite(confidenceScore) ? confidenceScore : relevanceScore,
        type: 'highlight',
        score: Number.isFinite(relevanceScore) ? relevanceScore.toFixed(3) : null,
        distance: null,
        duration: this.formatDuration(durationSeconds),
        headline: String(highlight?.label || highlight?.highlight_label || 'Suggested moment'),
        summary: `Peak ${this.formatDuration(timestamp)} • Duration ${this.formatDuration(durationSeconds)}`,
        description: String(
          highlight?.summary
          || highlight?.label
          || 'A suggested moment from this ceremony.'
        ),
        metadata: `Confidence ${Number.isFinite(confidenceScore) ? confidenceScore.toFixed(3) : 'n/a'} • Relevance ${Number.isFinite(relevanceScore) ? relevanceScore.toFixed(3) : 'n/a'} • Supporting hits ${Number.isFinite(supportingHits) ? supportingHits : 'n/a'} • Supporting queries ${Number.isFinite(supportingQueryCount) ? supportingQueryCount : 'n/a'}`,
        tags: [
          highlight?.kind === 'ceremony_moment' ? 'ceremony moment' : 'standout',
          Number.isFinite(confidenceScore) ? `confidence ${confidenceScore.toFixed(3)}` : 'confidence n/a',
          Number.isFinite(relevanceScore) ? `relevance ${relevanceScore.toFixed(3)}` : 'relevance n/a',
        ],
      }
    },
    parseDurationToSeconds(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return value
      if (!value || typeof value !== 'string') return 0
      const parts = value.split(':').map(part => Number(part))
      if (parts.some(part => !Number.isFinite(part))) return 0
      if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2]
      if (parts.length === 2) return (parts[0] * 60) + parts[1]
      return parts[0]
    },
    clearEventPlaybackTimer() {
      if (this.eventPlaybackTimer) {
        clearTimeout(this.eventPlaybackTimer)
        this.eventPlaybackTimer = null
      }
    },
    pauseWorkspacePlayer() {
      this.clearEventPlaybackTimer()
      this.$refs.mediaPlayer?.player?.pause()
    },
    getSafeSeekTime(player, requestedSeconds) {
      const requested = Number(requestedSeconds)
      if (!Number.isFinite(requested)) return null

      const seekable = player?.seekable?.()
      if (seekable && seekable.length) {
        const rangeStart = Number(seekable.start(0))
        const rangeEnd = Number(seekable.end(seekable.length - 1))
        if (Number.isFinite(rangeStart) && Number.isFinite(rangeEnd) && rangeEnd > rangeStart) {
          const bufferedEnd = Math.max(rangeStart, rangeEnd - 0.5)
          return Math.min(Math.max(requested, rangeStart), bufferedEnd)
        }
      }

      const duration = Number(player?.duration?.())
      if (Number.isFinite(duration) && duration > 0) {
        return Math.min(Math.max(requested, 0), Math.max(0, duration - 0.5))
      }

      return Math.max(requested, 0)
    },
    playEventPreview(event) {
      const player = this.$refs.mediaPlayer?.player
      if (!player || !event) return

      const requestedStartSeconds = Number(event.windowStartSeconds ?? event.timestamp)
      const durationSeconds = this.parseDurationToSeconds(event.duration)
      const startSeconds = this.getSafeSeekTime(player, requestedStartSeconds)
      if (!Number.isFinite(startSeconds)) return

      this.trackProductEvent('preview_started', {
        ...this.resultTrackingMetadata(event),
      })
      this.clearEventPlaybackTimer()
      player.pause()

      const beginPlayback = () => {
        const playPromise = player.play()
        if (playPromise?.catch) {
          playPromise.catch(() => {})
        }
      }

      player.one('seeked', beginPlayback)
      player.currentTime(startSeconds)

      if (durationSeconds > 0) {
        this.eventPlaybackTimer = setTimeout(() => {
          player.pause()
          this.eventPlaybackTimer = null
        }, durationSeconds * 1000)
      }
    },
    async selectWorkspaceItem(event) {
      if (!event?.id) return
      const activityType = ['frame', 'transcript'].includes(event.type)
        ? 'search_result_selected'
        : (event.type === 'highlight'
            ? 'suggested_moment_selected'
            : 'ceremony_chapter_selected')
      this.trackProductEvent(activityType, {
        ...this.resultTrackingMetadata(event),
      })
      await this.loadResultCeremony(event)
      this.selectedEventId = event.id
      await this.$nextTick()
      this.playEventPreview(event)
    },
    async loadResultCeremony(event) {
      const ceremonyId = String(event?.ceremonyId || '')
      if (!ceremonyId || ceremonyId === this.selectedCeremonyId) return
      this.selectedCeremonyId = ceremonyId
      await this.fetchCeremonyHighlights()
      if (this.eventsData.some(result => result.id === event.id)) {
        this.selectedEventId = event.id
      }
    },
    resultCeremonyLabel(ceremonyId) {
      const ceremony = this.ceremonies.find(item => String(item.id) === String(ceremonyId))
      return ceremony ? this.formatCeremony(ceremony) : ''
    },
    async selectHighlight(highlight) {
      const event = this.mapHighlightToEvent(highlight)
      if (!event) return
      await this.selectWorkspaceItem(event)
    },
    formatCeremony(ceremony) {
      if (!ceremony) return 'Unknown ceremony'
      const datetime = ceremony.datetime ? this.formatDateTime(ceremony.datetime) : 'No date'
      const venue = ceremony.venue ? ` • ${ceremony.venue}` : ''
      return `${datetime}${venue}`
    },
    formatDuration(totalSeconds) {
      const safeSeconds = Math.max(0, Math.round(totalSeconds || 0))
      const hours = Math.floor(safeSeconds / 3600)
      const minutes = Math.floor((safeSeconds % 3600) / 60)
      const seconds = safeSeconds % 60
      if (hours > 0) {
        return [hours, minutes, seconds].map((part, index) => String(part).padStart(index === 0 ? 1 : 2, '0')).join(':')
      }
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    showCeremonyGuide() {
      this.hasSubmittedSearch = false
      this.eventsUploadError = ''
      const selectedIsSearchResult = this.eventsData.some(
        event => event.id === this.selectedEventId
      )
      if (selectedIsSearchResult) {
        this.selectedEventId = this.suggestedEvents[0]?.id || this.guideChapters[0]?.id || ''
      }
    },
    submitSearch() {
      if (this.eventsLoading) return
      if (!this.canSubmitSearch) return
      this.currentSearchId = this.newSearchId()
      this.trackProductEvent('semantic_search_submitted', {
        searchId: this.currentSearchId,
        ceremonyId: this.selectedCeremonyId,
        searchScope: this.searchScope,
        ceremonyCount: this.searchCeremonyIds.length,
        universitySlug: this.selectedSlug,
        queryLength: String(this.queryText || '').length,
        topK: this.top_k,
      })
      this.hasSubmittedSearch = true
      this.loadEventsAPI()
    },
    async recordEventFeedback({ eventId, response }) {
      if (!eventId || !response) return
      if (this.eventFeedback[eventId] || this.feedbackSubmitting[eventId]) return
      const event = this.eventsData.find(row => row.id === eventId)
      this.feedbackSubmitting = {
        ...this.feedbackSubmitting,
        [eventId]: true,
      }
      this.feedbackErrors = {
        ...this.feedbackErrors,
        [eventId]: '',
      }
      const saved = await this.trackProductEvent('feedback_submitted', {
        ...this.resultTrackingMetadata(event),
        response,
        relativeRelevanceScore: event?.relative_relevance_score ?? null,
      })
      this.feedbackSubmitting = {
        ...this.feedbackSubmitting,
        [eventId]: false,
      }
      if (saved) {
        this.eventFeedback = {
          ...this.eventFeedback,
          [eventId]: { response },
        }
        return
      }
      this.feedbackErrors = {
        ...this.feedbackErrors,
        [eventId]: 'Feedback could not be saved. Please try again.',
      }
    },
  },
  created() {
    this.restoreEventExports()
    this.refreshRecentExports()
    this.trackProductEvent('ceremony_intelligence_opened')
    this.initialLoad()
  },
  mounted() {
    if (this.showWelcome) {
      this.$nextTick(() => this.$refs.welcomeDismiss?.focus())
    }
  },
  beforeDestroy() {
    this.clearEventPlaybackTimer()
    this.clearExportPolling()
    this.clearContextPolling()
  },
}
</script>

<style scoped>
.intelligence-page {
  max-width: 1360px;
  margin: 0 auto;
  padding: 12px 24px 40px;
}

.page-heading {
  max-width: 650px;
  margin-bottom: 24px;
}

.page-eyebrow {
  margin: 0 0 6px;
  color: #7c3aed;
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.page-heading h2 {
  margin: 0;
  color: #151a28;
  font-size: clamp(1.8rem, 3vw, 2.55rem);
  font-weight: 750;
  letter-spacing: -0.035em;
  line-height: 1.08;
}

.page-heading > p:last-child {
  margin: 10px 0 0;
  color: #667085;
  font-size: 0.92rem;
}

.welcome-backdrop {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: grid;
  padding: 24px;
  place-items: center;
  background: rgba(15, 23, 42, 0.56);
  backdrop-filter: blur(4px);
}

.welcome-modal {
  display: grid;
  width: min(100%, 610px);
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  padding: 28px;
  border: 1px solid #ddd6fe;
  border-radius: 22px;
  background: linear-gradient(120deg, #faf8ff, #fff);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.28);
}

.welcome-mark {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 10px;
  background: #ede9fe;
  color: #7c3aed;
  font-size: 1rem;
}

.welcome-copy h3,
.welcome-copy p {
  margin: 0;
}

.welcome-copy h3 {
  color: #202534;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
}

.welcome-copy p {
  margin-top: 4px;
  color: #5f6879;
  font-size: 0.82rem;
  line-height: 1.55;
}

.welcome-copy .welcome-eyebrow {
  margin: 0 0 5px;
  color: #7c3aed;
  font-size: 0.68rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.welcome-tips {
  margin-top: 16px;
  padding: 14px 15px;
  border-radius: 12px;
  background: #f5f3ff;
}

.welcome-tips h4 {
  margin: 0 0 9px;
  color: #3b246b;
  font-size: 0.76rem;
  font-weight: 750;
}

.welcome-tips ul {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.welcome-tips li {
  position: relative;
  padding-left: 14px;
  color: #5f6879;
  font-size: 0.8rem;
  line-height: 1.45;
}

.welcome-tips li::before {
  position: absolute;
  top: 0.47em;
  left: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8b5cf6;
  content: '';
}

.welcome-tips strong {
  margin-right: 4px;
  color: #3b246b;
}

.welcome-copy .welcome-help {
  color: #4c1d95;
  font-weight: 600;
}

.welcome-dismiss {
  grid-column: 2;
  min-width: 128px;
  min-height: 44px;
  justify-self: start;
  padding: 0 18px;
  border: 1px solid #6d28d9;
  border-radius: 10px;
  background: #6d28d9;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.welcome-dismiss:hover {
  background: #5b21b6;
}

.search-surface {
  border: 1px solid #e4e7ed;
  border-radius: 18px;
  padding: 22px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(31, 41, 55, 0.05);
}

.recent-exports {
  display: grid;
  grid-template-columns: minmax(220px, 0.7fr) minmax(0, 2fr);
  align-items: center;
  gap: 24px;
  margin-top: 18px;
  padding: 18px 20px;
  border: 1px solid #e4e7ed;
  border-radius: 16px;
  background: #fff;
}

.recent-exports-heading,
.recent-export-item,
.recent-export-actions {
  display: flex;
  align-items: center;
}

.recent-exports-heading {
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
}

.recent-exports-eyebrow {
  margin: 0 0 3px;
  color: #7c3aed;
  font-size: 0.66rem;
  font-weight: 750;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.recent-exports-heading h3,
.recent-exports-heading p {
  margin: 0;
}

.recent-exports-heading h3 {
  color: #202534;
  font-size: 1rem;
}

.recent-exports-heading > p,
.recent-export-copy span {
  color: #697386;
  font-size: 0.78rem;
}

.recent-export-list {
  display: flex;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.recent-export-item {
  flex: 1 1 0;
  min-width: 0;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #f7f8fb;
}

.recent-export-copy {
  min-width: 0;
}

.recent-export-copy strong,
.recent-export-copy span {
  display: block;
}

.recent-export-copy strong {
  overflow: hidden;
  color: #202534;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-export-copy span {
  margin-top: 4px;
}

.recent-export-actions {
  flex: 0 0 auto;
  gap: 8px;
}

.recent-export-status {
  color: #667085;
  font-size: 0.68rem;
  font-weight: 700;
}

.recent-export-status.is-ready {
  color: #067647;
}

.recent-export-status.is-attention {
  color: #b54708;
}

.recent-export-button {
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid #d7dce5;
  border-radius: 9px;
  background: #fff;
  color: #252b3a;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
}

.recent-export-button:hover:not(:disabled) {
  border-color: #8b5cf6;
  color: #6d28d9;
}

.recent-export-button:disabled {
  cursor: default;
  opacity: 0.58;
}

.suggested-section {
  margin-top: 24px;
}

.ceremony-workspace {
  display: grid;
  grid-template-columns: minmax(250px, 0.82fr) minmax(480px, 2fr) minmax(250px, 0.82fr);
  align-items: start;
  gap: 16px;
  margin-top: 22px;
}

.workspace-guide,
.workspace-details {
  min-width: 0;
  max-height: 620px;
  overflow-y: auto;
  border-radius: 16px;
}

.workspace-player {
  min-width: 0;
}

@media (max-width: 1199px) {
  .intelligence-page {
    padding-inline: 18px;
  }

  .ceremony-workspace {
    grid-template-columns: minmax(250px, 0.8fr) minmax(440px, 1.5fr);
  }

  .workspace-guide {
    grid-row: 1 / span 2;
  }

  .workspace-details {
    max-height: none;
  }

  .recent-export-list {
    flex-direction: column;
  }

  .recent-exports {
    display: block;
  }

  .recent-exports-heading {
    margin-bottom: 12px;
  }
}

@media (max-width: 899px) {
  .intelligence-page {
    padding: 8px 14px 32px;
  }

  .page-heading {
    margin-bottom: 18px;
  }

  .search-surface {
    padding: 17px;
  }

  .welcome-backdrop {
    align-items: end;
    padding: 12px;
  }

  .welcome-modal {
    gap: 13px;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
    padding: 22px 18px 20px;
    border-radius: 20px;
  }

  .welcome-dismiss {
    width: 100%;
  }

  .recent-exports {
    padding: 16px;
  }

  .recent-exports-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .recent-export-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .recent-export-actions {
    width: 100%;
    justify-content: space-between;
  }

  .recent-export-button {
    min-height: 44px;
  }

  .ceremony-workspace {
    grid-template-columns: 1fr;
  }

  .workspace-player {
    grid-row: 1;
  }

  .workspace-guide {
    grid-row: 2;
    max-height: none;
    overflow: visible;
  }

  .workspace-details {
    grid-row: 3;
    overflow: visible;
  }
}
</style>
